import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND, buildWaLink } from "@/lib/constants";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/packages", label: "Packages" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isHero = isHome && !scrolled; // transparent over dark hero

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Color tokens that change with hero/non-hero state
  const wordmarkClass = isHero ? "text-[#FAF4ED]" : "text-[#1F3A5F]";
  const taglineClass = isHero ? "text-[#FAF4ED]/75" : "text-[#5A6B82]";
  const navIdleClass = isHero
    ? "text-[#FAF4ED]/85 hover:text-[#FAF4ED]"
    : "text-[#5A6B82] hover:text-[#1F3A5F]";
  const navActiveClass = isHero
    ? "text-[#FAF4ED] font-medium"
    : "text-[#1F3A5F] font-medium";
  const ctaBtnClass = isHero
    ? "bg-[#E8B89B] hover:bg-[#DDA98B] text-[#1F3A5F]"
    : "bg-[#1F3A5F] hover:bg-[#11233B] text-[#FAF4ED]";
  const burgerClass = isHero ? "text-[#FAF4ED]" : "text-[#1F3A5F]";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        isHero
          ? "bg-transparent"
          : "bg-[#FAF4ED]/90 backdrop-blur-xl border-b border-[#1F3A5F]/10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-24">
        <Link
          to="/"
          data-testid="brand-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={isHero ? BRAND.logoLight : BRAND.logoTransparent}
            alt="iExplore"
            className="h-16 w-16 md:h-20 md:w-20 object-contain transition-all duration-300"
          />
          <span className="hidden sm:flex flex-col leading-none">
            <span
              className={`font-serif text-2xl md:text-3xl tracking-tight transition-colors ${wordmarkClass}`}
            >
              iExplore
            </span>
            <span
              className={`text-[10px] tracking-[0.28em] uppercase mt-1 transition-colors ${taglineClass}`}
            >
              Explore Beyond Limits
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              end={item.to === "/"}
              className={({ isActive }) =>
                `text-sm uppercase tracking-[0.18em] transition-colors link-underline ${
                  isActive ? navActiveClass : navIdleClass
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <a
          href={buildWaLink()}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="header-whatsapp-cta"
          className={`hidden md:inline-flex items-center px-6 py-3 text-xs uppercase tracking-[0.18em] transition-colors ${ctaBtnClass}`}
        >
          Plan My Trip
        </a>

        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          className={`md:hidden p-2 transition-colors ${burgerClass}`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#FAF4ED] border-t border-[#1F3A5F]/10"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mnav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base uppercase tracking-[0.18em] ${
                    isActive ? "text-[#1F3A5F] font-medium" : "text-[#5A6B82]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={buildWaLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-cta"
              className="mt-2 inline-flex items-center justify-center bg-[#1F3A5F] text-[#FAF4ED] px-6 py-4 text-xs uppercase tracking-[0.18em]"
            >
              Plan My Trip on WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
